import { useEffect, useState } from "react";
import { useHardConfirm } from "../../components/HardConfirmDialog";
import type {
  CreateVenueBlackoutWindowInput,
  ProviderBlackoutWindow,
} from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";

const emptyDraft: CreateVenueBlackoutWindowInput = {
  start_local: "",
  end_local: "",
  note: "",
};

function formatBlackoutDateTime(value: string, timeZone: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

export function BlackoutWindowsCard({
  deletingBlackoutWindowId,
  isSubmitting,
  onCreate,
  onDelete,
  timezone,
  value,
}: {
  deletingBlackoutWindowId: string | null;
  isSubmitting: boolean;
  onCreate: (value: CreateVenueBlackoutWindowInput) => Promise<unknown>;
  onDelete: (blackoutId: string) => Promise<void>;
  timezone: string;
  value: ProviderBlackoutWindow[];
}) {
  const { resolvedLanguage, t } = useAppPreferences();
  const locale = resolvedLanguage === "ar" ? "ar-EG" : "en-US";
  const { confirm, dialog } = useHardConfirm();
  const [form, setForm] = useState<CreateVenueBlackoutWindowInput>(emptyDraft);

  useEffect(() => {
    setForm(emptyDraft);
  }, [timezone, value.length]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onCreate(form);
  };

  const handleDelete = async (window: ProviderBlackoutWindow) => {
    const isConfirmed = await confirm({
      confirmationValue: "REMOVE",
      confirmButtonLabel: t("Remove blackout"),
      consequence: t("Bookings, walk-ins, and session extensions can use this time block again as soon as the venue refreshes."),
      description: t("This removes the blackout scheduled from {start} to {end}.", { start: formatBlackoutDateTime(window.start_at, timezone, locale), end: formatBlackoutDateTime(window.end_at, timezone, locale) }),
      title: t("Remove blackout window?"),
    });
    if (!isConfirmed) {
      return;
    }

    await onDelete(window.id);
  };

  return (
    <div className="ops-surface">
      {dialog}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="ops-kicker">{t("Blackout windows")}</p>
          <p className="mt-1 text-xs leading-4 text-slate-400">
            {t("Block bookings, walk-ins, and session extensions during maintenance, private events, or venue-wide closures.")}
          </p>
        </div>
        <div className="ops-chip">{t("{count} scheduled", { count: value.length })}</div>
      </div>

      <form className="mt-4 grid gap-3" onSubmit={(event) => void handleSubmit(event)}>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="ops-field">
            <span className="ops-label">{t("Starts at")}</span>
            <input
              className="ops-input"
              onChange={(event) => {
                setForm((current) => ({ ...current, start_local: event.target.value }));
              }}
              required
              type="datetime-local"
              value={form.start_local}
            />
          </label>
          <label className="ops-field">
            <span className="ops-label">{t("Ends at")}</span>
            <input
              className="ops-input"
              onChange={(event) => {
                setForm((current) => ({ ...current, end_local: event.target.value }));
              }}
              required
              type="datetime-local"
              value={form.end_local}
            />
          </label>
        </div>

        <label className="ops-field">
          <span className="ops-label">{t("Reason")}</span>
          <textarea
            className="ops-textarea min-h-16"
            onChange={(event) => {
              setForm((current) => ({ ...current, note: event.target.value }));
            }}
            placeholder={t("Deep clean, private booking, hardware migration...")}
            value={form.note ?? ""}
          />
        </label>

        <button
          className="ops-button"
          data-size="sm"
          data-variant="primary"
          disabled={isSubmitting}
          type="submit"
        >
          {isSubmitting ? t("Saving blackout...") : t("Add blackout window")}
        </button>
      </form>

      <div className="mt-4 grid gap-2">
        {value.length > 0 ? (
          value.map((window) => (
            <div className="ops-record ops-record-dense" key={window.id}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">
                    {formatBlackoutDateTime(window.start_at, timezone, locale)} {t("to")}{" "}
                    {formatBlackoutDateTime(window.end_at, timezone, locale)}
                  </p>
                  <p className="text-xs leading-4 text-slate-400">
                    {window.note ?? t("No reason added yet.")}
                  </p>
                </div>
                <button
                  className="ops-button"
                  data-size="sm"
                  data-variant="danger"
                  disabled={deletingBlackoutWindowId === window.id}
                  onClick={() => void handleDelete(window)}
                  type="button"
                >
                  {deletingBlackoutWindowId === window.id ? t("Removing...") : t("Remove")}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="ops-empty">
            {t("No blackout windows yet. Add one when the venue needs maintenance time or a private closure block.")}
          </div>
        )}
      </div>
    </div>
  );
}
